// $lib/stores/searchHistoryStore.ts
import { writable, derived } from "svelte/store";
import { browser } from "$app/environment";
import { authStore } from "./authStore";
import type { Readable } from "svelte/store";
import type { SearchMetadata, GroupedSearches, User } from "$lib/types";

export class SearchHistoryManager {
  public static readonly STORAGE_KEY = "searchHistory";
  public static readonly ANONYMOUS_USER = "anonymous";

  /**
   * Gets user identifier for storing searches
   */
  static getUserIdentifier(user: User | undefined): string {
    if (!user) return this.ANONYMOUS_USER;
    return user.id || user.email || this.ANONYMOUS_USER;
  }

  /**
   * Merges new searches with existing ones, preserving order and removing duplicates
   */
  static mergeSearches(existing: SearchMetadata[], incoming: SearchMetadata[]): SearchMetadata[] {
    const searchMap = new Map<string, SearchMetadata>();

    incoming.forEach((search) => {
      searchMap.set(search.slug, search);
    });
    existing.forEach((search) => {
      searchMap.set(search.slug, search);
    });

    return Array.from(searchMap.values()).sort(
      (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
    );
  }

  // /**
  //  * Loads search history from localStorage
  //  */
  // static loadStoredHistory(): SearchMetadata[] {
  //   if (!browser) return [];

  //   try {
  //     const storedHistory = localStorage.getItem(this.STORAGE_KEY);
  //     if (!storedHistory) return [];

  //     const parsedHistory = JSON.parse(storedHistory, (key, value) => (key === "updatedAt" ? new Date(value) : value));

  //     return this.attachUserIdToHistory(parsedHistory);
  //   } catch (error) {
  //     console.error("Error loading search history:", error);
  //     return [];
  //   }
  // }

  /**
   * Attaches user ID to historical searches
   */
  private static attachUserIdToHistory(history: SearchMetadata[]): SearchMetadata[] {
    const currentUser = authStore.getUser();
    const userID = this.getUserIdentifier(currentUser);

    return history.map((search) => (search.userID ? search : { ...search, userID }));
  }

  /**
   * Saves search history to localStorage
   */
  static saveToLocalStorage(searches: SearchMetadata[]): void {
    if (!browser) return;

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(searches));
    } catch (error) {
      console.error("Error saving search history:", error);
    }
  }

  /**
   * Gets time-based group label for a search
   */
  static getTimeGroupLabel(date: Date): string {
    if (isNaN(date.getTime())) return "Other";

    const timeFrames = this.getTimeFrames();

    if (date >= timeFrames.today) return "Today";
    if (date >= timeFrames.yesterday) return "Yesterday";
    if (date >= timeFrames.lastWeek) return "Last week";
    if (date >= timeFrames.lastMonth) return "Last month";

    return date.toLocaleString("default", { month: "long", year: "numeric" });
  }

  /**
   * Gets relevant time frames for grouping
   */
  private static getTimeFrames() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    return { today, yesterday, lastWeek, lastMonth };
  }
}

// Create the base store
const { subscribe, update } = writable<SearchMetadata[]>([]);

// Create and export the main store
export const searchHistoryStore = {
  subscribe,
  addSearch(search: SearchMetadata) {
    update((searches) => {
      const userID = SearchHistoryManager.getUserIdentifier(authStore.getUser());

      const newSearch = {
        ...search,
        createdAt: new Date(),
        updatedAt: new Date(),
        userID,
      } as SearchMetadata;

      const updatedSearches = [newSearch, ...searches.filter((s) => s.slug !== search.slug)];

      // SearchHistoryManager.saveToLocalStorage(updatedSearches);
      return updatedSearches;
    });
  },

  updateSearch(slug: string, updates: Partial<SearchMetadata>) {
    update((searches) => {
      const userID = SearchHistoryManager.getUserIdentifier(authStore.getUser());

      const updatedSearches = searches.map((search) =>
        search.slug === slug && (search.userID === userID || !search.userID)
          ? { ...search, ...updates, updatedAt: new Date(), userID }
          : search,
      );

      // SearchHistoryManager.saveToLocalStorage(updatedSearches);
      return updatedSearches;
    });
  },

  clearHistory() {
    update((searches) => {
      const userID = SearchHistoryManager.getUserIdentifier(authStore.getUser());
      const updatedSearches = searches.filter((search) => search.userID !== userID);

      // SearchHistoryManager.saveToLocalStorage(updatedSearches);
      return updatedSearches;
    });
  },

  removeSearch(slug: string) {
    update((searches) => {
      if (!authStore.getUser()) return searches;

      const updatedSearches = searches.filter((s) => s.slug !== slug);
      // SearchHistoryManager.saveToLocalStorage(updatedSearches);
      return updatedSearches;
    });
  },

  migrateAnonymousHistory(user: User) {
    update((searches) => {
      const userID = SearchHistoryManager.getUserIdentifier(user);

      const updatedSearches = searches.map((search) =>
        search.userID === SearchHistoryManager.ANONYMOUS_USER ? { ...search, userID } : search,
      );

      // SearchHistoryManager.saveToLocalStorage(updatedSearches);
      return updatedSearches;
    });
  },

  /**
   * Sets the entire search history for the current user
   */
  setSearches(searches: SearchMetadata[]) {
    update((existingSearches) => {
      const userID = SearchHistoryManager.getUserIdentifier(authStore.getUser());

      // Filter out existing searches for the current user
      const otherUserSearches = existingSearches.filter((search) => search.userID !== userID);

      // Add the new searches with the current user ID
      const newSearches = searches.map((search) => ({
        ...search,
        userID,
      }));

      const updatedSearches = [...newSearches, ...otherUserSearches];
      // SearchHistoryManager.saveToLocalStorage(updatedSearches);
      return updatedSearches;
    });
  },

  /**
   * Appends new searches to the existing history
   */
  appendSearches(newSearches: SearchMetadata[]) {
    update((existingSearches) => {
      const userID = SearchHistoryManager.getUserIdentifier(authStore.getUser());

      // Prepare new searches with user ID
      const processedNewSearches = newSearches.map((search) => ({
        ...search,
        userID,
      }));

      // Merge existing and new searches
      const mergedSearches = SearchHistoryManager.mergeSearches(existingSearches, processedNewSearches);

      // SearchHistoryManager.saveToLocalStorage(mergedSearches);
      return mergedSearches;
    });
  },

  /**
   * Gets searches for the current user
   */
  getSearchesForCurrentUser(): SearchMetadata[] {
    const userID = SearchHistoryManager.getUserIdentifier(authStore.getUser());
    let searches: SearchMetadata[] = [];

    // Use subscribe to get current value
    const unsubscribe = subscribe((currentSearches) => {
      searches = currentSearches.filter((search) => search.userID === userID);
    });

    // Clean up subscription
    unsubscribe();

    return searches;
  },

  /**
   * Gets the most recent search for the current user
   */
  getLatestSearch(): SearchMetadata | undefined {
    const userID = SearchHistoryManager.getUserIdentifier(authStore.getUser());
    let latestSearch: SearchMetadata | undefined;

    const unsubscribe = subscribe((currentSearches) => {
      const userSearches = currentSearches.filter((search) => search.userID === userID);
      latestSearch = userSearches.length > 0 ? userSearches[0] : undefined;
    });

    unsubscribe();

    return latestSearch;
  },
};

// Helper function to group searches consistently
export function groupSearches(searches: SearchMetadata[]): GroupedSearches {
  const groups: GroupedSearches = {};

  searches.forEach((search) => {
    const createdAt = search.createdAt ? new Date(search.createdAt) : new Date();
    const groupLabel = SearchHistoryManager.getTimeGroupLabel(createdAt);

    if (!groups[groupLabel]) {
      groups[groupLabel] = [];
    }
    groups[groupLabel].push(search);
  });

  return groups;
}
